import { Component, Host, h, Element, Prop, State, Event, EventEmitter } from "@stencil/core";

import { StzhCtaFocusEvent, StzhCtaBlurEvent, StzhCtaClickEvent } from "../../index";

import { hasSlot } from "../../utils/utils";

var counter = 0;

/**
 * @slot heading - Slot for heading as alternative for attribute
 * @slot lead - Slot for lead as alternative for attribute
 * @slot label - Slot for button label as alternative for attribute
 */
@Component({
  tag: "stzh-cta",
  styleUrl: "stzh-cta.scss",
  scoped: true,
})
export class StzhCta {
  /** Heading */
  @Prop() heading: string = "";

  /** Lead */
  @Prop() lead: string = "";

  /** Button Label */
  @Prop() label: string = "";

  /** Button link */
  @Prop() href: string = "";

  /**
   *  Whether sticky button is disabled.
   *  Will be set to true automatically on,
   *  when more than one cta is on the page.
   */
  @Prop() stickyDisabled: boolean = true;

  @State() loaded: boolean = false;

  @Element() element: HTMLStzhCtaElement;

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhCtaFocusEvent>;

  /** Blur event */
  @Event() stzhBlur: EventEmitter<StzhCtaBlurEvent>;

  /** Click event */
  @Event() stzhClick: EventEmitter<StzhCtaClickEvent>;

  private button: HTMLButtonElement | HTMLAnchorElement;
  private placeholderButton: HTMLElement;
  private debounceResize: number;
  private focusedByInput: boolean = false;
  private placeholderButtonResizeObserver: ResizeObserver;

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.button.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-cta",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-cta",
      originalEvent: event,
    });
  };

  private onClick = (event: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-cta",
      originalEvent: event,
      href: this.href,
    });
  };

  private handlePlaceholderButtonResize = () => {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.updateButtonWidthByResize);
  };

  private updateButtonWidthByResize = () => {
    if (!this.placeholderButton) {
      return;
    }

    const { width } = this.placeholderButton.getBoundingClientRect();
    this.element.style.setProperty("--button-width", `${width}px`);
  };

  componentDidLoad() {
    this.stickyDisabled = counter > 1;

    this.placeholderButtonResizeObserver = new ResizeObserver(this.handlePlaceholderButtonResize);
    if (this.placeholderButton) {
      this.placeholderButtonResizeObserver.observe(this.placeholderButton);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateButtonWidthByResize();

        window.setTimeout(() => {
          this.loaded = true;
        }, 250);
      });
    });
  }

  componentDidRender() {
    if (this.placeholderButton && this.placeholderButtonResizeObserver) {
      this.placeholderButtonResizeObserver.observe(this.placeholderButton);
    }
  }

  connectedCallback() {
    counter = counter + 1;
  }

  disconnectedCallback() {
    // current no way of sending events in disconnected callback, so we just decrease the counter now,
    // but the user has to set stickyDisabled=false by himself
    counter = counter - 1;

    this.placeholderButtonResizeObserver?.disconnect();
  }

  private renderButtons(size: "default" | "small") {
    return (
      <div class="stzh-cta__buttons">
        {size === "small" && (
          <div class="stzh-cta__button is-placeholder" ref={el => (this.placeholderButton = el as HTMLElement)}>
            <span class="stzh-cta__button-text">{this.label}</span>
            <stzh-icon class="stzh-cta__button-icon" name="arrow-right"></stzh-icon>
          </div>
        )}

        {this.href ? (
          <a
            class="stzh-cta__button"
            href={this.href}
            ref={el => (this.button = el as HTMLAnchorElement)}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
          >
            <span class="stzh-cta__button-text">{this.label}</span>
            <stzh-icon class="stzh-cta__button-icon" name="arrow-right"></stzh-icon>
          </a>
        ) : (
          <button
            class="stzh-cta__button"
            ref={el => (this.button = el as HTMLButtonElement)}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          >
            <span class="stzh-cta__button-text">{this.label}</span>
            <stzh-icon class="stzh-cta__button-icon" name="arrow-right"></stzh-icon>
          </button>
        )}
      </div>
    );
  }

  render() {
    const headingSlotUsed = hasSlot(this.element, "heading");
    const leadSlotUsed = hasSlot(this.element, "lead");

    const hasHeading = !!this.heading || headingSlotUsed;
    const hasLead = !!this.lead || leadSlotUsed;
    const hasHeadingOrLead = hasHeading || hasLead;

    const size = !hasHeadingOrLead ? "small" : "default";

    const classes = {
      "stzh-cta": true,
      "stzh-cta--is-sticky-disabled": this.stickyDisabled,
      "stzh-cta--is-not-loaded": !this.loaded,
      "stzh-cta--has-heading": hasHeading,
      "stzh-cta--has-lead": hasLead,
      [`stzh-cta--size-${size}`]: !!size,
    };

    return (
      <Host size={size} tabindex="-1" onFocus={this.onRootFocus}>
        <article class={classes} data-stzh-sticky-width-container>
          <div class="stzh-cta__wrapper">
            <div class="stzh-cta__heading">{this.heading ? this.heading : <slot name="heading"></slot>}</div>
            <div class="stzh-cta__lead">{this.lead ? this.lead : <slot name="lead"></slot>}</div>
            <stzh-sticky
              name="cta"
              class="stzh-cta__sticky"
              position="fixed"
              bleeding
              disableStickyBottom={this.stickyDisabled}
              disableStickyTop={true}
            >
              {this.renderButtons(size)}
            </stzh-sticky>
          </div>
        </article>
      </Host>
    );
  }
}
